import { RcFile } from "antd/es/upload";

import { axios, persistentQueryClient } from "utils";

import { ADMIN_CARBON_PROGRAM_URL, GET_PROGRAM_CONTRACTS_ADMIN } from "consts";

import {
  AddOrUpdateCarbonProgramFeatureFlagRequestBody,
  CarbonProgram,
  CarbonProgramFeatureFlagsResponse,
  CarbonProgramOutcomesResponse,
  CarbonProgramParticipation,
  CarbonProgramParticipationRequest,
  CarbonProgramWithFarmCount,
  GetCarbonProgramFeatureFlagsRequestBody,
  IncentivesPotentialPayouts,
  ProgramContract,
} from "models";

interface GetFarmsParams {
  params: {
    limit: number;
    page: number;
    name?: string;
    carbonProgramId?: string;
  };
}

export const getAllCarbonPrograms = async (): Promise<CarbonProgramWithFarmCount[]> => axios.get(ADMIN_CARBON_PROGRAM_URL);

export const getCarbonProgramByIdAdmin = async (programId?: string): Promise<CarbonProgram> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${ADMIN_CARBON_PROGRAM_URL}/${programId}`, { hideErrorMessage: true });

export const getFarmsByCarbonProgram = async ({ params }: GetFarmsParams): Promise<{ participations: CarbonProgramParticipation[]; count: number }> =>
  axios.get(`${ADMIN_CARBON_PROGRAM_URL}/farms`, { params });

export const getCarbonProgramByFarmIdAdmin = async (farmId?: string): Promise<CarbonProgram> =>
  // @ts-expect-error custom params can be passed in axios config
  axios.get(`${ADMIN_CARBON_PROGRAM_URL}/farms/${farmId}`, { hideErrorMessage: true });

export const addOrUpdateProgramParticipation = async (data: CarbonProgramParticipationRequest): Promise<CarbonProgramParticipation> =>
  axios.put(`${ADMIN_CARBON_PROGRAM_URL}/farms`, { data });

export const deleteProgramParticipation = async (data: { farmId: string }): Promise<void> => axios.delete(`${ADMIN_CARBON_PROGRAM_URL}/farms`, { data });

export const getProgramContracts = async (programId: string): Promise<ProgramContract[]> => axios.get(`${ADMIN_CARBON_PROGRAM_URL}/${programId}/contracts`);

const addProgramContract = async (data: { programId: string; description: string | null; file: string | ArrayBuffer | null }): Promise<{ filename: string }> =>
  axios.post(`${ADMIN_CARBON_PROGRAM_URL}/${data.programId}/contracts`, { data });

export const parseProgramContract = async (data: { programId: string; description: string | null; file: RcFile }) => {
  const reader = new FileReader();

  return new Promise((resolve, reject) => {
    reader.readAsDataURL(data.file);

    reader.onerror = () => {
      reader.abort();
      reject(new DOMException("Problem parsing input file."));
    };

    reader.onload = () => {
      addProgramContract({
        file: reader.result,
        programId: data.programId,
        description: data.description,
      }).then((res) => {
        persistentQueryClient.invalidateQueries({ queryKey: [GET_PROGRAM_CONTRACTS_ADMIN, data.programId] });
        resolve(res);
      });
    };
  });
};

export const updateProgramContractDescription = async (data: { programId: string; contractId: string; description: string | null }): Promise<ProgramContract> =>
  axios.put(`${ADMIN_CARBON_PROGRAM_URL}/${data.programId}/contracts/${data.contractId}/description`, { data: { description: data.description } });

export const getCarbonProgramOutcomesAdmin = async (data: { fieldIds: string[]; isVerification?: boolean }): Promise<CarbonProgramOutcomesResponse> =>
  axios.post(`${ADMIN_CARBON_PROGRAM_URL}/run`, { data });

export const getIncentivesPotentialPayoutsAdmin = async (data: { farmId: string }): Promise<IncentivesPotentialPayouts> =>
  axios.get(`${ADMIN_CARBON_PROGRAM_URL}/farms/${data.farmId}/incentives-potential-payouts`);

// feature flags

export const getCarbonProgramFeatureFlagsAdmin = async (data: GetCarbonProgramFeatureFlagsRequestBody): Promise<CarbonProgramFeatureFlagsResponse[]> =>
  axios.get(`${ADMIN_CARBON_PROGRAM_URL}/feature-flags/${data.programId}`);

export const addOrUpdateCarbonProgramFeatureFlagAdmin = async (
  data: AddOrUpdateCarbonProgramFeatureFlagRequestBody
): Promise<CarbonProgramFeatureFlagsResponse> => axios.put(`${ADMIN_CARBON_PROGRAM_URL}/feature-flags`, { data });
